import React from 'react';
import { Body, Label } from '@sato/components';
import { observer } from 'mobx-react-lite';

import { IWidgetFields } from '@/contentful/types';

import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { FacilityReservationSystem } from '@models/realEstate.types';
import { apartmentStore } from '@stores/apartmentStore';

export type SaunaReservationSystemTranslations = {
  reservationSystem: Record<
    FacilityReservationSystem,
    { name: string; description: string }
  >;
};

const translationsTypeGuard = (
  translations: Props['translations']
): translations is SaunaReservationSystemTranslations => {
  return !!translations && 'reservationSystem' in translations;
};

type Props = {
  translations: IWidgetFields['customTranslations'];
};

/** Describes which reservation system saunas in the selected real estate use. */
export const SaunaReservationSystem = observer((props: Props) => {
  const { t } = useCustomTranslations(props.translations);

  const realEstateSauna =
    apartmentStore.selectedApartment?.realestate.realEstateFeatureLocations.find(
      (location) => location.feature === 'REAL_ESTATE_SAUNA'
    );

  const reservationSystem = realEstateSauna?.reservation?.reservationSystem;
  // Early escape if...
  if (
    // there is no reservation system described in the real estate
    !reservationSystem ||
    // translation JSON is malformed
    !translationsTypeGuard(props.translations) ||
    // this reservation system does not have translations defined in the JSON
    !props.translations.reservationSystem[reservationSystem]
  ) {
    return null;
  }

  return (
    <div className="mb-3 py-3 first:pt-0">
      <Label component="h3" className="mb-2">
        {t(`reservationSystem.${reservationSystem}.name`)}
      </Label>

      <Body>{t(`reservationSystem.${reservationSystem}.description`)}</Body>
    </div>
  );
});
